import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toDataURL } from "qrcode";

import { useApi } from "../api/client";
import "./ExitSurveyQr.css";

/**
 * Shown in the assessment wizard after an EXIT assessment is saved. Mints a
 * single-use exit-survey link (deliver:"link" — no SMS, no consent, no mobile
 * needed) and renders it as a QR the kaimahi can hold up for the whānau to
 * scan on the spot, plus the raw link to copy. Independent of the SMS path.
 */

type Props = { whanauId: number; onClose: () => void };

type State =
  | { kind: "loading" }
  | { kind: "ready"; link: string; dataUrl: string }
  | { kind: "error" };

export function ExitSurveyQr({ whanauId, onClose }: Props) {
  const { t } = useTranslation();
  const callApi = useApi();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await callApi<{ link: string }>(
          `/whanau/${String(whanauId)}/exit-survey/invite`,
          {
            method: "POST",
            body: JSON.stringify({ deliver: "link" }),
            headers: { "content-type": "application/json" },
          },
        );
        const dataUrl = await toDataURL(res.link, { width: 240, margin: 1 });
        if (!cancelled) setState({ kind: "ready", link: res.link, dataUrl });
      } catch {
        if (!cancelled) setState({ kind: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [whanauId, callApi]);

  return (
    <div className="exit-qr">
      <h3 className="exit-qr__title">{t("wizard.exitQr.title")}</h3>
      <p className="exit-qr__hint">{t("wizard.exitQr.hint")}</p>

      {state.kind === "loading" && (
        <p className="exit-qr__status">{t("wizard.exitQr.loading")}</p>
      )}
      {state.kind === "error" && (
        <p className="exit-qr__error">{t("wizard.exitQr.error")}</p>
      )}
      {state.kind === "ready" && (
        <>
          <img
            className="exit-qr__img"
            src={state.dataUrl}
            alt={t("wizard.exitQr.alt")}
            width={240}
            height={240}
          />
          <div className="exit-qr__link-row">
            <input
              className="exit-qr__link"
              type="text"
              readOnly
              value={state.link}
              onFocus={(e) => e.currentTarget.select()}
              aria-label={t("wizard.exitQr.linkLabel")}
            />
            <button
              type="button"
              className="exit-qr__copy"
              onClick={() => {
                void navigator.clipboard
                  .writeText(state.link)
                  .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  })
                  .catch(() => {
                    /* clipboard blocked — the field is selectable to copy */
                  });
              }}
            >
              {copied ? t("wizard.exitQr.copied") : t("wizard.exitQr.copy")}
            </button>
          </div>
        </>
      )}

      <button type="button" className="exit-qr__done" onClick={onClose}>
        {t("wizard.exitQr.done")}
      </button>
    </div>
  );
}

export default ExitSurveyQr;
